// Sentinel workspace queries and composer state.

import { atom, useAtom, useAtomValue, useSetAtom, type Getter } from 'jotai';
import { atomWithInfiniteQuery, atomWithQuery } from 'jotai-tanstack-query';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';
import {
  activeRefineLabelFromApi,
  canRegenerateFromApi,
  classifyTraceStepKind,
  inflightTraceFromApi,
  mergeTraceSteps,
  traceHistoryFromApi,
  SentinelWorkspaceAdapter,
  type SentinelArtifact,
} from '@/adapters/sentinelWorkspace.adapter';
import {
  sentinelWorkspaceService,
  SentinelWorkspaceError,
  type RejectInput,
  type SendResponseOutcome,
} from '@/services/sentinelWorkspace.service';
import { integrationService } from '@/services/integration.service';
import { aiSettingsService } from '@/services/ai-settings.service';
import { accountAiSettingsQueryAtom } from '@/hooks/use-ai-settings';
import {
  MAX_RESPONSE_REFINES,
  REGENERATE_LABEL,
  type Blueprint,
  type FeedbackVerdict,
  type GeneratedResponse,
  type PendingAction,
  type SentinelConversation,
  type SentinelOutboundAttachment,
  type SentinelTicketListWireDto,
  type TicketOutcome,
  type TraceStep,
  type WorkspaceMessage,
  type CustomerTicketDto,
  type GateCheckDto,
} from '@/modules/sentinel-workspace/types';
import { sentinelErrorMessage } from '@/modules/sentinel-workspace/error-copy';
import { buScopeParams } from '@/lib/business-unit-scope';
import { SENTINEL_TICKET_PARAM, SENTINEL_WORKSPACE_PATH, sentinelTicketIdFromUrl, sentinelTicketPath } from '@/lib/landing';
import { nestBulletsUnderNumbers } from '@/modules/sentinel-workspace/sentinel-workspace.helpers';
import { currentUserAtom } from '@/hooks/use-auth';
import { resolvedSentinelSectionAtom, selectedSentinelSectionAtom, sentinelTicketStatusAtom } from '@/hooks/use-sentinel-routing';
import { queryClient } from '@/lib/query-client';
import { getApiErrorMessage } from '@/lib/utils';
import { captureException } from '@/lib/sentry/sentry-utils';
import { markOwnTicketAction } from '@/lib/sentinel-own-actions';
import { TAGS } from '@/constants/sentry-constants';
import { toast } from '@/components/ui/sonner';
import {
  sentinelThinkingStepsAtom,
  setSentinelWorkspaceSocketIdentityAtom,
} from '@/stores/websocket.store';
import type { SentinelWorkspaceDetailWireDto } from '@/modules/sentinel-workspace/types';
import type { TicketFieldValue } from '@/types';

export const selectedTicketIdAtom = atom<number | null>(null);
export const ticketListCollapsedAtom = atom(false);
/** The helpdesk ticket number the list is narrowed to; empty lists the whole queue. */
const ticketSearchAtom = atom('');
const TICKET_SEARCH_DEBOUNCE_MS = 300;

// 'awaiting_hitl' also covers the run after an approval — the backend keeps that status while Brahma executes.
const POLLING_STATUSES = new Set([
  'ingesting',
  'pending',
  'awaiting_hitl',
  'response_generating',
  'dispatching',
]);

// Keeps the query polling during locally-driven runs, whose cached status never enters POLLING_STATUSES.
// Reference-counted so one unmounting workspace instance cannot turn off another's polling.
const sentinelComposerBusyCountAtom = atom(0);
export const sentinelComposerBusyAtom = atom((get) => get(sentinelComposerBusyCountAtom) > 0);

export function sentinelConversationPollInterval(
  detail: SentinelWorkspaceDetailWireDto | undefined,
  composerBusy = false,
): number | false {
  if (composerBusy) return 1_000;
  if (detail?.ticket.status === 'awaiting_customer') return 30_000;
  return detail && POLLING_STATUSES.has(detail.ticket.status) ? 1_000 : false;
}

// The backend caps a page at 100; 50 fills the panel twice over.
const TICKETS_PAGE_SIZE = 50;

/** The brand the ticket list is narrowed to: the one selected at the top, in every tab. */
function ticketListBrandParams(get: Getter): { bu_id?: string } {
  return buScopeParams(get);
}

function ticketListBrandKey(get: Getter): string {
  return ticketListBrandParams(get).bu_id ?? 'all';
}

const ticketsQueryAtom = atomWithInfiniteQuery((get) => {
  const user = get(currentUserAtom);
  const accountId = user?.accountId == null ? null : Number(user.accountId);
  return {
    // The brand is in the key as well as the request. Switching brands does not clear the
    // cache, so a key without it would serve the previous brand's page of tickets — and
    // this list is paginated, so a stale first page also poisons the offsets that follow.
    queryKey: [
      'sentinel-workspace',
      'tickets',
      accountId,
      ticketListBrandKey(get),
      get(resolvedSentinelSectionAtom),
      get(sentinelTicketStatusAtom),
      get(ticketSearchAtom),
    ],
    queryFn: ({ pageParam }: { pageParam?: number }) => {
      const section = get(resolvedSentinelSectionAtom);
      const brand = ticketListBrandParams(get);
      const search = get(ticketSearchAtom);
      // The status filter belongs to the routed views; the original queue has none.
      const ticketStatus = section ? (get(sentinelTicketStatusAtom) ?? undefined) : undefined;
      if (search) {
        return sentinelWorkspaceService.getTickets(TICKETS_PAGE_SIZE, pageParam ?? 0, brand, section ?? undefined, search, ticketStatus);
      }
      // Routing off: exactly the original request, with no section.
      return section
        ? sentinelWorkspaceService.getTickets(TICKETS_PAGE_SIZE, pageParam ?? 0, brand, section, undefined, ticketStatus)
        : sentinelWorkspaceService.getTickets(TICKETS_PAGE_SIZE, pageParam ?? 0, brand);
    },
    initialPageParam: 0 as number | undefined,
    // Ask from where the list ends, until it holds them all.
    getNextPageParam: (lastPage: SentinelTicketListWireDto, allPages: SentinelTicketListWireDto[]) => {
      const loaded = allPages.reduce((total, page) => total + page.rows.length, 0);
      return loaded < lastPage.count ? loaded : undefined;
    },
    enabled: accountId !== null,
    refetchOnWindowFocus: false,
    staleTime: 60_000,
  };
});

const conversationQueryAtom = atomWithQuery<SentinelWorkspaceDetailWireDto>((get) => {
  const id = get(selectedTicketIdAtom);
  const user = get(currentUserAtom);
  const accountId = user?.accountId == null ? null : Number(user.accountId);
  const composerBusy = get(sentinelComposerBusyAtom);
  // One cache entry per ticket — putting the stream/full variant in the key swaps
  // to an empty entry on every busy toggle, which flashes the whole pane.
  //
  // Deliberately NOT keyed by brand: a ticket id already names exactly one ticket and
  // therefore one brand, so the answer cannot differ between brands. Adding the brand here
  // would only split one ticket's cache across brands and refetch it on every switch.
  const queryKey = ['sentinel-workspace', 'conversation', accountId, id];
  return {
    queryKey,
    queryFn: async () => {
      const detail = await sentinelWorkspaceService.getConversation(id as number, !composerBusy);
      // Light stream polls omit messages — keep the cached thread so it doesn't flicker away.
      if (detail.messages === undefined) {
        const cached = queryClient.getQueryData<SentinelWorkspaceDetailWireDto>(queryKey);
        if (cached?.messages) detail.messages = cached.messages;
      }
      return detail;
    },
    enabled: id !== null && accountId !== null,
    refetchOnWindowFocus: false,
    // A ticket can advance while unselected — always reconcile cached detail on return.
    staleTime: 0,
    refetchInterval: (query) => sentinelConversationPollInterval(query.state.data, composerBusy),
  };
});

/** The list's ticket total, for the view switch shown outside the list too (the Follow-ups section). */
export const sentinelTicketTotalAtom = atom((get) => get(ticketsQueryAtom).data?.pages.at(-1)?.count);

/** The open ticket's brand. Switching brands keeps the ticket open, so the switcher's brand can be another. */
function openTicketBrand(get: Getter): string | null {
  const buId = get(conversationQueryAtom).data?.ticket.conversation.bu_id;
  return buId == null || buId === '' ? null : String(buId);
}

const ticketAiSettingsQueryAtom = atomWithQuery((get) => {
  const brand = openTicketBrand(get);
  return {
    queryKey: ['brand', 'ai-settings', 'ticket', brand ?? 'account'],
    queryFn: () => aiSettingsService.getAiSettings(brand ? { bu_id: brand } : undefined),
    enabled: get(conversationQueryAtom).data !== undefined,
    refetchOnWindowFocus: false,
    staleTime: 5 * 60_000,
  };
});

const ticketSentinelSettingsQueryAtom = atomWithQuery((get) => {
  const brand = openTicketBrand(get);
  return {
    queryKey: ['sentinel-workspace', 'ticket-settings', brand ?? 'account'],
    queryFn: () => sentinelWorkspaceService.getSettings(brand ? { bu_id: brand } : {}),
    enabled: get(conversationQueryAtom).data !== undefined,
    refetchOnWindowFocus: false,
    staleTime: 5 * 60_000,
  };
});

/**
 * How the open ticket reads, from its own brand's settings: the timezone (else the account's,
 * which a brand override replaces whole) and whether it plans a Blueprint. Unknown until loaded.
 */
export function useTicketDisplaySettings(): { timeZone?: string; blueprintEnabled?: boolean } {
  const [brand] = useAtom(ticketAiSettingsQueryAtom);
  const [account] = useAtom(accountAiSettingsQueryAtom);
  const [sentinel] = useAtom(ticketSentinelSettingsQueryAtom);
  return {
    timeZone: brand.data?.operatingHours?.timezone || account.data?.operatingHours?.timezone || undefined,
    blueprintEnabled: sentinel.data?.blueprintEnabled,
  };
}

// The open ticket's brand rows plus account-level ones (machine returns both for a bu_id).
const subscriptionIntegrationsQueryAtom = atomWithQuery((get) => {
  const user = get(currentUserAtom);
  const accountId = user?.accountId == null ? null : Number(user.accountId);
  const detail = get(conversationQueryAtom).data;
  const buId = detail?.ticket.conversation.bu_id;
  const brand = buId == null || buId === '' ? null : String(buId);
  return {
    queryKey: ['sentinel-workspace', 'subscription-integrations', accountId, brand ?? 'account'],
    queryFn: async () => {
      const integrations = await integrationService.getAccountIntegrations(brand ? { bu_id: brand } : undefined);
      return integrations.map((integration) => integration.name);
    },
    enabled: accountId !== null && detail !== undefined,
    refetchOnWindowFocus: false,
    staleTime: 5 * 60_000,
  };
});

/** The open ticket's brand integrations, which decide its subscription provider. Null while loading; undefined if the lookup failed. */
export function useSubscriptionIntegrations(): string[] | null | undefined {
  const [query] = useAtom(subscriptionIntegrationsQueryAtom);
  if (query.isError) return undefined;
  return query.data ?? null;
}

/** Ticket list for the left panel. The open ticket lives in the URL so a refresh keeps it. */
export function useSentinelTickets() {
  const [query] = useAtom(ticketsQueryAtom);
  const [selectedId, setSelectedId] = useAtom(selectedTicketIdAtom);
  const [ticketSearch, setTicketSearch] = useAtom(ticketSearchAtom);
  const [searchInput, setSearchInput] = useState('');
  const { pathname, search } = useLocation();
  const navigate = useNavigate();

  // Debounced so typing a ticket number sends one request, not one per digit.
  useEffect(() => {
    const timer = setTimeout(() => setTicketSearch(searchInput.trim()), TICKET_SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [searchInput, setTicketSearch]);
  // The open ticket lives in the path, so a refresh or a shared link reopens it.
  const urlTicketId = sentinelTicketIdFromUrl(pathname, new URLSearchParams(search));

  useEffect(() => {
    if (urlTicketId !== selectedId) setSelectedId(urlTicketId);
  }, [selectedId, setSelectedId, urlTicketId]);

  // Old `?ticket=` links and paths naming no ticket move to the canonical path.
  useEffect(() => {
    const params = new URLSearchParams(search);
    const canonical = urlTicketId === null ? SENTINEL_WORKSPACE_PATH : sentinelTicketPath(urlTicketId);
    if (pathname === canonical && !params.has(SENTINEL_TICKET_PARAM)) return;
    params.delete(SENTINEL_TICKET_PARAM);
    navigate(`${canonical}${params.size ? `?${params}` : ''}`, { replace: true });
  }, [navigate, pathname, search, urlTicketId]);

  const seenIdsRef = useRef<Set<number> | null>(null);
  const [unreadTicketIds, setUnreadTicketIds] = useState<ReadonlySet<number>>(new Set());
  // A new search swaps the list, so the next load is a fresh baseline rather than new arrivals.
  useEffect(() => {
    seenIdsRef.current = null;
  }, [ticketSearch]);
  useEffect(() => {
    // First page only: the later ones are older tickets, not new arrivals.
    const firstPage = query.data?.pages[0];
    if (!firstPage || ticketSearch) return;
    const ids = new Set(firstPage.rows.map((row) => Number(row.id)));
    const seen = seenIdsRef.current;
    seenIdsRef.current = ids;
    // The first load is the baseline — only later arrivals count as unread.
    if (!seen) return;
    const fresh = [...ids].filter((id) => !seen.has(id));
    if (fresh.length) setUnreadTicketIds((prev) => new Set([...prev, ...fresh]));
  }, [query.data, ticketSearch]);

  const selectTicket = useCallback(
    (ticketId: number | null) => {
      setUnreadTicketIds((prev) => {
        if (ticketId === null || !prev.has(ticketId)) return prev;
        const next = new Set(prev);
        next.delete(ticketId);
        return next;
      });
      setSelectedId(ticketId);
      navigate(`${ticketId === null ? SENTINEL_WORKSPACE_PATH : sentinelTicketPath(ticketId)}${search}`, { replace: true });
    },
    [navigate, search, setSelectedId],
  );

  // A different list closes the open ticket, which may not be in it. Only the viewer's own picks
  // count (the picked view, the status filter), not the view the page resolves on load, so a
  // refresh on a ticket keeps it open.
  const listKey = `${useAtomValue(selectedSentinelSectionAtom)}:${useAtomValue(sentinelTicketStatusAtom)}`;
  const listKeyRef = useRef(listKey);
  useEffect(() => {
    if (listKeyRef.current === listKey) return;
    listKeyRef.current = listKey;
    if (urlTicketId !== null) selectTicket(null);
  }, [listKey, selectTicket, urlTicketId]);

  // Switching brand deliberately does NOT close the open ticket, even though the list is
  // narrowed to the brand and the ticket may leave it: closing it would take an agent off
  // the ticket they were working on because they glanced at another brand.

  // Rebuilt rows would redraw the whole list on every poll.
  const tickets = useMemo(
    () => SentinelWorkspaceAdapter.ticketsFromApi(query.data?.pages.flatMap((page) => page.rows) ?? []),
    [query.data],
  );

  // Kept stable, or the list re-observes and pages again on every render.
  const { fetchNextPage } = query;
  const loadMore = useCallback(() => void fetchNextPage(), [fetchNextPage]);

  return {
    tickets,
    // The latest page carries the freshest total.
    totalCount: query.data?.pages.at(-1)?.count ?? 0,
    isLoading: query.isLoading,
    hasMore: query.hasNextPage,
    isFetchingNextPage: query.isFetchingNextPage,
    loadMore,
    // No background list refresh — the header offers a manual one instead.
    refresh: query.refetch,
    // Paging is not refreshing, so it must not spin the header button.
    isRefreshing: query.isFetching && !query.isLoading && !query.isFetchingNextPage,
    unreadTicketIds,
    selectedId,
    selectTicket,
    search: searchInput,
    setSearch: setSearchInput,
  };
}

/** Conversation detail for the selected ticket. */
export function useSentinelConversation(): {
  conversation: SentinelConversation | null;
  detail: SentinelWorkspaceDetailWireDto | null;
  isLoading: boolean;
  error: unknown;
  /** Re-read the ticket — for an action that changes it outside the composer's own flow. */
  refresh: () => Promise<unknown>;
} {
  const selectedId = useAtomValue(selectedTicketIdAtom);
  const [query] = useAtom(conversationQueryAtom);
  // A new object each render would redo all the work that depends on it.
  const conversation = useMemo(
    () => (query.data ? SentinelWorkspaceAdapter.conversationFromApi(query.data) : null),
    [query.data],
  );

  if (selectedId === null)
    return { conversation: null, detail: null, isLoading: false, error: null, refresh: query.refetch };
  return {
    conversation,
    detail: query.data ?? null,
    isLoading: query.isLoading,
    error: query.data ? null : query.error,
    refresh: query.refetch,
  };
}

// ---------- Composer state machine ----------

interface ComposerProgress {
  trace: TraceStep[];
  stepsDone: number;
  expanded: boolean;
}

export type ComposerState =
  | { status: 'idle' }
  | ({ status: 'blueprintLoading' } & ComposerProgress)
  | { status: 'blueprint'; blueprint: Blueprint; refineOpen: boolean }
  | ({ status: 'blueprintRefining'; blueprint: Blueprint } & ComposerProgress)
  | ({ status: 'generating' } & ComposerProgress)
  | { status: 'waitingForCustomer' }
  // Keep the failed action so retry returns to its card.
  | {
      status: 'generationError';
      message: string;
      retryable: boolean;
      manual?: boolean;
      failedAction?: PendingAction;
      /** Why Kim handed the ticket to a person, when it did. */
      reason?: string;
    }
  | { status: 'response'; active: GeneratedResponse }
  | {
      status: 'automated';
      response: GeneratedResponse;
      delivery: Extract<SentinelArtifact, { kind: 'automated' }>['delivery'];
      message: string | null;
    }
  | { status: 'action'; action: PendingAction; armed: boolean }
  | { status: 'actionRunning'; action: PendingAction; label?: string }
  | {
      status: 'editing';
      draftMarkdown: string;
      sourceResponseId: string | null;
      /** The automation card the draft was taken from, to go back to; it has no refine chat. */
      automated?: Extract<ComposerState, { status: 'automated' }>;
    }
  | { status: 'done'; label: 'answered' | TicketOutcome; canGenerate?: boolean; generating?: boolean };

type ComposerProgressState = Extract<
  ComposerState,
  { status: 'blueprintLoading' | 'blueprintRefining' | 'generating' }
>;

type ComposerPollingState = Extract<ComposerState, { status: 'blueprintLoading' | 'generating' }>;

function isComposerProgressState(state: ComposerState): state is ComposerProgressState {
  return (
    state.status === 'blueprintLoading' ||
    state.status === 'blueprintRefining' ||
    state.status === 'generating'
  );
}

function isComposerPollingState(state: ComposerState): state is ComposerPollingState {
  return state.status === 'blueprintLoading' || state.status === 'generating';
}

export type WorkspaceModal =
  | 'feedback'
  | 'rejectBlueprint'
  | 'rejectResponse'
  | 'assign'
  | 'sendGate'
  | 'overrideReason'
  | null;

/** Status/modal values as constants so callers never compare raw strings. */
export const COMPOSER_STATUS = {
  idle: 'idle',
  blueprintLoading: 'blueprintLoading',
  blueprint: 'blueprint',
  blueprintRefining: 'blueprintRefining',
  generating: 'generating',
  waitingForCustomer: 'waitingForCustomer',
  generationError: 'generationError',
  response: 'response',
  automated: 'automated',
  action: 'action',
  actionRunning: 'actionRunning',
  editing: 'editing',
  done: 'done',
} as const;

/** Artifacts each in-flight state accepts from a poll — anything it is not still producing itself. */
const ADOPTABLE_FROM_POLL: Partial<Record<ComposerState['status'], readonly SentinelArtifact['kind'][]>> = {
  blueprintLoading: ['blueprint', 'hitl', 'response', 'automated', 'error'],
  blueprintRefining: ['hitl', 'response', 'automated', 'error'],
  generating: ['hitl', 'response', 'automated', 'error'],
  actionRunning: ['hitl', 'response', 'automated', 'error'],
  // Accept an approval that follows the first one.
  action: ['hitl'],
  automated: ['automated'],
};

export const WORKSPACE_MODAL = {
  feedback: 'feedback',
  rejectBlueprint: 'rejectBlueprint',
  rejectResponse: 'rejectResponse',
  assign: 'assign',
  sendGate: 'sendGate',
  overrideReason: 'overrideReason',
} as const;

/** Every run the ticket has already used, sorted so the same set never looks like a change. */
function knownRequestIds(detail: SentinelWorkspaceDetailWireDto): string[] {
  const ids = detail.aiEventLogs.map((event) => event.request_id);
  ids.push(detail.ticket.active_generation_request_id);
  return [...new Set(ids.filter((id): id is string => Boolean(id)))].sort();
}

/** A reply the Sentinel sent past the gate: its text, the button, why, and the close fields given. */
export interface GateOverride {
  content: string;
  close: boolean;
  reason: string;
  ticketFields: TicketFieldValue[];
}

export function useSentinelComposer(
  conversation: SentinelConversation | null,
  detail: SentinelWorkspaceDetailWireDto | null = null,
) {
  const [composer, setComposer] = useState<ComposerState>({ status: COMPOSER_STATUS.idle });
  const selectedTicketId = useAtomValue(selectedTicketIdAtom);
  const [modal, setModal] = useState<WorkspaceModal>(null);
  const [versions, setVersions] = useState<GeneratedResponse[]>([]);
  // The ticket whose AI view is open.
  const [aiChatTicketId, setAiChatTicketId] = useState<number | null>(null);
  // Streams trace steps while the refine runs, like the generating card.
  const [pendingRefine, setPendingRefine] = useState<{ label: string; trace: TraceStep[]; stepsDone: number } | null>(
    null,
  );
  const [refineError, setRefineError] = useState<string | null>(null);
  // Set while the shared AI overlay is collecting "what should Kim do instead" for a HITL action.
  const [rejectDraftAction, setRejectDraftAction] = useState<PendingAction | null>(null);
  // Which refine ask produced each response version — rendered as chat bubbles.
  const [refineLabels, setRefineLabels] = useState<Record<string, string>>({});
  const lastRefineRef = useRef<{ input: { command?: string; prompt?: string }; label: string } | null>(null);
  // True while a refine request is in flight — blocks concurrent refines.
  const refineBusyRef = useRef(false);
  const [refinesUsed, setRefinesUsed] = useState(0);
  // Caps come from Settings → Sentinel Workspace.
  const [maxResponseRefines, setMaxResponseRefines] = useState(MAX_RESPONSE_REFINES);
  // Null until the account settings answer — a guessed cap renders the wrong button first.
  const [maxBlueprintRefines, setMaxBlueprintRefines] = useState<number | null>(null);
  const [blueprintRefined, setBlueprintRefined] = useState(false);
  // The reply sent this session, appended to the thread until a refetch returns it.
  const [sentReply, setSentReply] = useState<string | null>(null);
  // The AI draft that reply came from, so its bubble links to its run before any refetch; null when written by hand.
  const [sentResponseId, setSentResponseId] = useState<string | null>(null);
  // Private notes added this session — same idea as sentReply.
  const [privateNotes, setPrivateNotes] = useState<WorkspaceMessage[]>([]);
  // Whether this ticket's brand checks a reply before it is sent.
  const [sendGateEnabled, setSendGateEnabled] = useState(false);
  // The last check, for the content it checked: Send carries its id, and the gate modal shows it.
  const [gateCheck, setGateCheck] = useState<{ content: string; check: GateCheckDto } | null>(null);
  // The reply the Sentinel overrode: the same message on the same button is not paused again.
  const [overridden, setOverridden] = useState<GateOverride | null>(null);
  const liveThinking = useAtomValue(sentinelThinkingStepsAtom);
  const setSocketIdentity = useSetAtom(setSentinelWorkspaceSocketIdentityAtom);
  const setComposerBusyCount = useSetAtom(sentinelComposerBusyCountAtom);

  // Ignore async results that land after the ticket changed.
  const runIdRef = useRef(0);
  // Reuse one key while the same reply is retried.
  const sendAttemptRef = useRef<{ signature: string; key: string; attachments: SentinelOutboundAttachment[] } | null>(
    null,
  );
  const conversationId = conversation?.id ?? null;
  // `conversationId` above is the Sentinel ticket's id. Settings are read by the conversation the ticket answers.
  const settingsConversationId = conversation?.conversationId ?? null;
  // Open for that ticket only, so switching hides it and coming back shows it.
  const aiChatOpen = aiChatTicketId !== null && aiChatTicketId === conversationId;
  const openTicketRef = useRef(conversationId);
  openTicketRef.current = conversationId;
  // Stable like a setState, so callers keep their own dependencies.
  const setAiChatOpen = useCallback((open: boolean) => setAiChatTicketId(open ? openTicketRef.current : null), []);
  const liveTrace: TraceStep[] = useMemo(
    () =>
      liveThinking.map((step) => {
        const detail = step.thinkingText ?? step.resultSummary ?? null;
        return {
          id: `${step.requestId}:${step.nodeId}`,
          kind: classifyTraceStepKind({
            nodeType: step.nodeType,
            toolName: step.toolName ?? null,
            nodeId: step.nodeId,
            label: step.label,
          }),
          title: step.label,
          detail: detail && detail !== step.label ? detail : null,
          done: step.done ?? false,
          ...(step.toolName ? { toolName: step.toolName } : {}),
          ...(step.toolProvider ? { toolProvider: step.toolProvider } : {}),
          ...(step.toolArgs ? { toolInput: step.toolArgs } : {}),
          ...('toolOutput' in step ? { toolOutput: step.toolOutput } : {}),
          ...(step.outcome ? { outcome: step.outcome } : {}),
          ...(step.durationMs !== undefined ? { durationMs: step.durationMs } : {}),
        };
      }),
    [liveThinking],
  );
  // State so the trace memo recomputes the moment a run is replaced — a ref kept stale steps.
  const [supersededTraceId, setSupersededTraceId] = useState<string | null>(null);
  const persistedTrace = useMemo(() => {
    if (!detail || detail.ticket.active_generation_trace_id === supersededTraceId) return [];
    return inflightTraceFromApi(detail);
  }, [detail, supersededTraceId]);
  const trace = useMemo(() => mergeTraceSteps(persistedTrace, liveTrace), [liveTrace, persistedTrace]);
  const toolTrace = useMemo(
    () => mergeTraceSteps(detail ? traceHistoryFromApi(detail) : [], liveTrace),
    [detail, liveTrace],
  );
  const traceRef = useRef(trace);
  traceRef.current = trace;
  // Refine progress ticks merge against the freshest socket steps, not a stale closure.
  const liveTraceRef = useRef(liveTrace);
  liveTraceRef.current = liveTrace;

  // The runs we have left — polls keep reporting them until the new one lands.
  const ignoredRequestsRef = useRef<string[]>([]);

  const updateSocketRequest = useCallback(
    (requestId: string | null) => {
      if (!detail) return;
      // Never re-adopt a run we just left.
      if (requestId !== null && ignoredRequestsRef.current.includes(requestId)) return;
      if (requestId !== null) ignoredRequestsRef.current = [];
      setSocketIdentity({
        ticketId: Number(detail.ticket.id),
        conversationId: detail.ticket.conversation_id,
        rootTraceId: detail.ticket.root_trace_id,
        requestId,
        ignoredRequestIds: ignoredRequestsRef.current,
      });
    },
    [detail, setSocketIdentity],
  );

  const awaitNextSocketRequest = useCallback(() => {
    if (!detail) return;
    // The detail lags the run we are starting, so its trace is the one we just left.
    setSupersededTraceId(detail.ticket.active_generation_trace_id);
    // The new run has no id yet, so every run already on the ticket counts as old.
    ignoredRequestsRef.current = knownRequestIds(detail);
    setSocketIdentity({
      ticketId: Number(detail.ticket.id),
      conversationId: detail.ticket.conversation_id,
      rootTraceId: detail.ticket.root_trace_id,
      requestId: null,
      ignoredRequestIds: ignoredRequestsRef.current,
    });
  }, [detail, setSocketIdentity]);

  useEffect(() => {
    if (!detail) {
      setSocketIdentity(null);
      return;
    }
    updateSocketRequest(detail.ticket.active_generation_request_id);
  }, [detail, setSocketIdentity, updateSocketRequest]);

  useEffect(() => () => setSocketIdentity(null), [setSocketIdentity]);

  useEffect(() => {
    setComposer((previous) =>
      isComposerProgressState(previous)
        ? { ...previous, trace, stepsDone: trace.length }
        : previous,
    );
    // Live socket steps only — the query detail is stale during a refine, so its
    // persisted trace belongs to the previous run; onProgress ticks add the fresh one.
    setPendingRefine((previous) => {
      if (!previous) return previous;
      const merged = mergeTraceSteps(previous.trace, liveTrace);
      return { ...previous, trace: merged, stepsDone: merged.length };
    });
  }, [liveTrace, trace]);

  // Poll while a local progress state runs so the persisted trace keeps streaming in.
  // Refines deliberately stay off this poll — the conversation effect resets refine
  // state on every detail change, so their steps stream via waitForArtifact ticks instead.
  useEffect(() => {
    if (!isComposerPollingState(composer)) return;
    setComposerBusyCount((count) => count + 1);
    return () => setComposerBusyCount((count) => count - 1);
    // Only composer.status controls the polling lifecycle.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [composer.status, setComposerBusyCount]);

  // Load the caps for every ticket — before, only one composer branch loaded them.
  //
  // Keyed on the TICKET, not the brand switcher. An open ticket can belong to another brand
  // than the one selected (a Sentinel's own queue spans all their brands, and switching
  // brand does not close the open ticket), and the backend enforces the
  // caps of the brand the conversation belongs to. Reading the switcher's brand here put a
  // number on screen the server disagreed with — a refine offered that it then refuses, or
  // one hidden that it would have allowed. Switching brand no longer reloads them, because
  // the open ticket has not changed.
  useEffect(() => {
    if (settingsConversationId === null) return;
    let cancelled = false;
    void sentinelWorkspaceService
      .getSettingsForConversation(settingsConversationId)
      .then((settings) => {
        if (cancelled) return;
        setMaxResponseRefines(settings.maxResponseRefines);
        setMaxBlueprintRefines(settings.maxBlueprintRefines);
        setSendGateEnabled(settings.sendGate?.enabled === true);
      })
      .catch((error) => captureException(error instanceof Error ? error : String(error), {
        tags: { [TAGS.ERROR_TYPE]: 'sentinel_load_settings' },
      }));
    return () => {
      cancelled = true;
    };
  }, [settingsConversationId]);

  const reportError = useCallback(
    (error: unknown, operation: string) => {
      captureException(error instanceof Error ? error : String(error), {
        tags: { [TAGS.ERROR_TYPE]: `sentinel_${operation}` },
        contexts: {
          sentinel: {
            conversationId: conversationId ?? undefined,
            operation,
            ...(error instanceof SentinelWorkspaceError
              ? { errorCode: error.code ?? undefined, errorDetail: error.detail ?? undefined }
              : {}),
          },
        },
      });
    },
    [conversationId],
  );

  const applyArtifact = useCallback((artifact: SentinelArtifact, replaceResponse = false) => {
    if (artifact.kind === 'hitl') {
      setComposer({ status: COMPOSER_STATUS.action, action: artifact.action, armed: false });
    } else if (artifact.kind === 'blueprint') {
      setComposer({ status: COMPOSER_STATUS.blueprint, blueprint: artifact.blueprint, refineOpen: false });
    } else if (artifact.kind === 'response') {
      setVersions((previous) => (replaceResponse ? [artifact.response] : [...previous, artifact.response]));
      setComposer({ status: COMPOSER_STATUS.response, active: artifact.response });
    } else if (artifact.kind === 'automated') {
      setVersions([artifact.response]);
      setComposer({
        status: COMPOSER_STATUS.automated,
        response: artifact.response,
        delivery: artifact.delivery,
        message: artifact.message,
      });
    } else if (artifact.kind === 'error') {
      setComposer({
        status: COMPOSER_STATUS.generationError,
        message: artifact.message,
        retryable: artifact.retryable,
        manual: artifact.manual,
        reason: artifact.reason,
      });
    }
  }, []);

  // A replanned refine is still owed an answer, however many blueprints it takes.
  const awaitingRefineRef = useRef(false);
  // The draft on screen when that refine started, so only a different one counts as its answer.
  const refineBaselineIdRef = useRef<string | null>(null);

  const applyDetail = useCallback(
    (nextDetail: SentinelWorkspaceDetailWireDto, replaceResponse = false) => {
      // The run is over, so a restored refine stops waiting.
      setPendingRefine(null);
      const artifact = SentinelWorkspaceAdapter.artifactFromApi(nextDetail);
      if (artifact.kind === 'response') {
        const history = SentinelWorkspaceAdapter.responseHistoryFromApi(nextDetail);
        if (history.versions.length > 0) {
          setVersions(history.versions);
          setRefineLabels(history.refineLabels);
          setComposer({ status: COMPOSER_STATUS.response, active: artifact.response });
          // The answer to a refine belongs back in the chat that asked for it.
          if (awaitingRefineRef.current && artifact.response.id !== refineBaselineIdRef.current) {
            awaitingRefineRef.current = false;
            setAiChatOpen(true);
          }
          return;
        }
      }
      applyArtifact(artifact, replaceResponse);
    },
    [applyArtifact, setAiChatOpen],
  );

  const generate = useCallback(async () => {
    if (conversationId === null) return;
    const runId = runIdRef.current;
    // Stepper starts expanded so the trace is visible; the header click collapses it.
    setComposer({ status: COMPOSER_STATUS.generating, trace: [], stepsDone: 0, expanded: true });
    awaitNextSocketRequest();

    try {
      const result = await sentinelWorkspaceService.generateResponse(conversationId, updateSocketRequest);
      if (runId !== runIdRef.current) return;
      applyDetail(result, true);
    } catch (error) {
      if (runId !== runIdRef.current) return;
      reportError(error, 'generate_response');
      setComposer({
        status: COMPOSER_STATUS.generationError,
        message: error instanceof Error ? error.message : 'Something went wrong',
        retryable: true,
        manual: true,
      });
    }
  }, [applyDetail, awaitNextSocketRequest, conversationId, reportError, updateSocketRequest]);

  /**
   * Generate on a ticket reopened in the helpdesk. The server restarts the cycle and drafts; the
   * workspace then follows it like any new turn, from the loading card.
   */
  const generateAgain = useCallback(async () => {
    if (conversationId === null) return;
    const runId = runIdRef.current;
    setComposer((current) =>
      current.status === COMPOSER_STATUS.done ? { ...current, generating: true } : current,
    );
    try {
      await sentinelWorkspaceService.generateAgain(conversationId);
      if (runId !== runIdRef.current) return;
      awaitNextSocketRequest();
      setComposer({ status: COMPOSER_STATUS.blueprintLoading, trace: [], stepsDone: 0, expanded: true });
    } catch (error) {
      if (runId !== runIdRef.current) return;
      reportError(error, 'generate_again');
      setComposer({
        status: COMPOSER_STATUS.generationError,
        message: getApiErrorMessage(error, 'Unable to generate a response for this ticket.'),
        retryable: false,
        manual: true,
      });
    }
  }, [awaitNextSocketRequest, conversationId, reportError]);

  const retryGeneration = useCallback(async () => {
    if (conversationId === null) return;
    const priorBlueprintVersion = Math.max(
      0,
      ...(detail?.aiEventLogs
        .filter((event) => event.event_kind === 'blueprint_revision')
        .map((event) => event.blueprint_revision_number ?? 0) ?? []),
    );
    const ingestionFailed = ['SENTINEL_ASSIGNMENT_FAILED', 'SENTINEL_CLAIM_FAILED'].includes(
      detail?.ticket.last_error_code ?? '',
    );
    if (priorBlueprintVersion > 0 && !ingestionFailed) {
      await generate();
      return;
    }

    const runId = runIdRef.current;
    setComposer({ status: COMPOSER_STATUS.blueprintLoading, trace: [], stepsDone: 0, expanded: true });
    awaitNextSocketRequest();
    try {
      const dto = await sentinelWorkspaceService.refineBlueprint(
        conversationId,
        'Regenerate the blueprint for this ticket.',
        priorBlueprintVersion,
        updateSocketRequest,
      );
      if (runId !== runIdRef.current) return;
      setComposer({
        status: COMPOSER_STATUS.blueprint,
        blueprint: SentinelWorkspaceAdapter.blueprintFromApi(dto),
        refineOpen: false,
      });
    } catch (error) {
      if (runId !== runIdRef.current) return;
      reportError(error, 'blueprint_retry');
      setComposer({
        status: COMPOSER_STATUS.generationError,
        message: getApiErrorMessage(error, 'Unable to retry blueprint generation.'),
        retryable: true,
        manual: true,
      });
    }
  }, [
    awaitNextSocketRequest,
    conversationId,
    detail?.aiEventLogs,
    detail?.ticket.last_error_code,
    generate,
    reportError,
    updateSocketRequest,
  ]);


  useEffect(() => {
    const runId = ++runIdRef.current;
    setModal(null);
    setVersions([]);
    setPendingRefine(null);
    setRefineError(null);
    setRefineLabels({});
    setRefinesUsed(0);
    setBlueprintRefined(false);
    setSentReply(null);
    setSentResponseId(null);
    setPrivateNotes([]);
    setGateCheck(null);
    setOverridden(null);
    lastRefineRef.current = null;
    awaitingRefineRef.current = false;
    sendAttemptRef.current = null;
    // The old refine's guard must not block a refine on the ticket now open.
    refineBusyRef.current = false;

    if (conversationId === null || settingsConversationId === null) {
      setComposer({ status: COMPOSER_STATUS.idle });
      return;
    }
    if (detail) {
      const artifact = SentinelWorkspaceAdapter.artifactFromApi(detail);
      if (artifact.kind === 'automated') {
        applyArtifact(artifact);
        return;
      }
      if (artifact.kind === 'response') {
        const history = SentinelWorkspaceAdapter.responseHistoryFromApi(detail);
        setVersions(history.versions.length > 0 ? history.versions : [artifact.response]);
        setRefineLabels(history.refineLabels);
        setRefinesUsed(detail.ticket.response_retries_used);
        setComposer({ status: COMPOSER_STATUS.response, active: artifact.response });
        return;
      }
      if (artifact.kind === 'hitl') {
        setComposer({ status: COMPOSER_STATUS.action, action: artifact.action, armed: false });
        return;
      }
      if (artifact.kind === 'blueprint') {
        setComposer({ status: COMPOSER_STATUS.blueprint, blueprint: artifact.blueprint, refineOpen: false });
        return;
      }
      if (artifact.kind === 'error') {
        setComposer({
          status: COMPOSER_STATUS.generationError,
          message: artifact.message,
          retryable: artifact.retryable,
          manual: artifact.manual,
          reason: artifact.reason,
        });
        return;
      }
      if (['ingesting', 'pending'].includes(detail.ticket.status)) {
        const currentTrace = traceRef.current;
        setComposer({
          status: COMPOSER_STATUS.blueprintLoading,
          trace: currentTrace,
          stepsDone: currentTrace.length,
          expanded: true,
        });
        return;
      }
      if (detail.ticket.status === 'response_generating') {
        const currentTrace = traceRef.current;
        // A refine is still running here, so show it again instead of a bare progress card.
        const refineLabel = activeRefineLabelFromApi(detail);
        if (refineLabel) {
          const history = SentinelWorkspaceAdapter.responseHistoryFromApi(detail);
          const trace = mergeTraceSteps(inflightTraceFromApi(detail), currentTrace);
          setVersions(history.versions);
          setRefineLabels(history.refineLabels);
          setRefinesUsed(detail.ticket.response_retries_used);
          setPendingRefine({ label: refineLabel, trace, stepsDone: trace.length });
          setAiChatOpen(true);
        }
        setComposer({ status: COMPOSER_STATUS.generating, trace: currentTrace, stepsDone: currentTrace.length, expanded: true });
        return;
      }
      if (detail.ticket.status === 'awaiting_customer') {
        setComposer({ status: COMPOSER_STATUS.waitingForCustomer });
        return;
      }
    }
    if (conversation?.outcome) {
      setComposer({
        status: COMPOSER_STATUS.done,
        label: conversation.outcome,
        canGenerate: conversation.canGenerate,
      });
      return;
    }

    const currentTrace = traceRef.current;
    setComposer({
      status: COMPOSER_STATUS.blueprintLoading,
      trace: currentTrace,
      stepsDone: currentTrace.length,
      expanded: true,
    });
    sentinelWorkspaceService
      .getSettingsForConversation(settingsConversationId)
      .then((settings) => {
        if (runId !== runIdRef.current) return;
        setMaxResponseRefines(settings.maxResponseRefines);
        setMaxBlueprintRefines(settings.maxBlueprintRefines);
        return sentinelWorkspaceService.getBlueprint(conversationId).then((dto) => {
          if (runId !== runIdRef.current) return;
          setComposer({
            status: COMPOSER_STATUS.blueprint,
            blueprint: SentinelWorkspaceAdapter.blueprintFromApi(dto),
            refineOpen: false,
          });
        });
      })
      .catch((error) => {
        if (runId !== runIdRef.current) return;
        reportError(error, 'initialise_workspace');
        setComposer({
          status: COMPOSER_STATUS.generationError,
          message: sentinelErrorMessage(null, 'blueprint'),
          retryable: true,
          manual: true,
        });
      });
    // Detail changes are handled by the polling effect below.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [applyArtifact, conversationId, settingsConversationId, conversation?.outcome, reportError]);

  // A poll can reveal that the run advanced — it may have been started elsewhere, or by
  // the server after ingestion. Adopt only what is genuinely ahead of the current state:
  // the detail lags a client-driven run, so 'generating' must ignore the blueprint it came from.
  const adoptedDetailRef = useRef<SentinelWorkspaceDetailWireDto | null>(null);
  useEffect(() => {
    if (!detail) return;
    // Only a fresh poll can reveal that: this also runs when the composer changes, and the
    // payload in hand then still predates the action that changed it.
    if (adoptedDetailRef.current === detail) return;
    adoptedDetailRef.current = detail;
    if (composer.status === COMPOSER_STATUS.blueprintLoading && detail.ticket.status === 'response_generating') {
      setComposer({ ...composer, status: COMPOSER_STATUS.generating });
      return;
    }
    const adoptable = ADOPTABLE_FROM_POLL[composer.status];
    if (!adoptable) return;

    const artifact = SentinelWorkspaceAdapter.artifactFromApi(detail);
    if (
      composer.status === COMPOSER_STATUS.automated &&
      artifact.kind === 'automated' &&
      composer.response.id === artifact.response.id &&
      composer.delivery === artifact.delivery &&
      composer.message === artifact.message
    ) {
      return;
    }
    // Keep the current state when the same approval returns.
    if (
      (composer.status === COMPOSER_STATUS.action || composer.status === COMPOSER_STATUS.actionRunning) &&
      artifact.kind === 'hitl' &&
      composer.action.id === artifact.action.id
    ) {
      return;
    }
    if (adoptable.includes(artifact.kind)) applyDetail(detail, true);
  }, [applyDetail, composer, detail]);

  const setRefineOpen = useCallback((open: boolean) => {
    setComposer((prev) => (prev.status === COMPOSER_STATUS.blueprint ? { ...prev, refineOpen: open } : prev));
  }, []);

  const refineBlueprint = useCallback(
    async (feedback: string) => {
      if (composer.status !== COMPOSER_STATUS.blueprint || conversationId === null) return;
      const { blueprint } = composer;
      const runId = runIdRef.current;
      setComposer({
        status: COMPOSER_STATUS.blueprintRefining,
        blueprint,
        trace: [],
        stepsDone: 0,
        expanded: true,
      });
      awaitNextSocketRequest();
      try {
        const dto = await sentinelWorkspaceService.refineBlueprint(
          conversationId,
          feedback,
          blueprint.version,
          updateSocketRequest,
        );
        if (runId !== runIdRef.current) return false;
        setBlueprintRefined(true);
        setComposer({
          status: COMPOSER_STATUS.blueprint,
          blueprint: SentinelWorkspaceAdapter.blueprintFromApi(dto),
          refineOpen: false,
        });
      } catch (error) {
        if (runId !== runIdRef.current) return false;
        setRefineError(getApiErrorMessage(error, 'Unable to refine the blueprint. Please try again.'));
        reportError(error, 'blueprint_refine');
        setComposer({ status: COMPOSER_STATUS.blueprint, blueprint, refineOpen: false });
      } finally {
        // Nothing polls after a refine, so refresh the ticket or its retry count stays behind.
        void queryClient.invalidateQueries({ queryKey: ['sentinel-workspace', 'conversation'] });
      }
    },
    [awaitNextSocketRequest, composer, conversationId, reportError, updateSocketRequest],
  );

  const toggleGenerationExpanded = useCallback(() => {
    setComposer((prev) =>
      isComposerProgressState(prev) ? { ...prev, expanded: !prev.expanded } : prev,
    );
  }, []);

  const armAction = useCallback(() => {
    setComposer((prev) => (prev.status === COMPOSER_STATUS.action ? { ...prev, armed: true } : prev));
  }, []);

  const confirmAction = useCallback(async () => {
    if (composer.status !== COMPOSER_STATUS.action || conversationId === null) return;
    const { action } = composer;
    const runId = runIdRef.current;
    setComposer({ status: COMPOSER_STATUS.actionRunning, action });
    awaitNextSocketRequest();
    try {
      const dto = await sentinelWorkspaceService.executeAction(conversationId, action.id, updateSocketRequest);
      if (runId !== runIdRef.current) return;
      applyDetail(dto);
    } catch (error) {
      if (runId !== runIdRef.current) return;
      reportError(error, 'hitl_action');
      setComposer({
        status: COMPOSER_STATUS.generationError,
        message: 'Error: unable to perform the action. Do you want to retry?',
        retryable: true,
        failedAction: action,
      });
    }
  }, [applyDetail, awaitNextSocketRequest, composer, conversationId, reportError, updateSocketRequest]);

  /** Open the shared AI overlay to ask what Kim should do instead of the pending action. */
  const openRejectDraft = useCallback(() => {
    if (composer.status !== COMPOSER_STATUS.action) return;
    setRejectDraftAction(composer.action);
    setRefineError(null);
    setAiChatOpen(true);
  }, [composer, setAiChatOpen]);

  const closeRejectDraft = useCallback(() => {
    setRejectDraftAction(null);
    setAiChatOpen(false);
  }, [setAiChatOpen]);

  /** Turn the action down with a note on what to do instead; Kim answers with a reply to edit. */
  const rejectActionWithDraft = useCallback(
    async (notes: string) => {
      const instruction = notes.trim();
      if (composer.status !== COMPOSER_STATUS.action || conversationId === null || !instruction) return;
      const { action } = composer;
      setRejectDraftAction(null);
      setAiChatOpen(false);
      const runId = runIdRef.current;
      setComposer({ status: COMPOSER_STATUS.actionRunning, action, label: 'Drafting a response...' });
      awaitNextSocketRequest();
      try {
        const dto = await sentinelWorkspaceService.rejectAction(
          conversationId,
          action.id,
          instruction,
          updateSocketRequest,
        );
        if (runId !== runIdRef.current) return;
        applyDetail(dto);
      } catch (error) {
        if (runId !== runIdRef.current) return;
        reportError(error, 'hitl_action');
        setComposer({
          status: COMPOSER_STATUS.generationError,
          message: 'Error: unable to reject the action. Do you want to retry?',
          retryable: true,
          failedAction: action,
        });
      }
    },
    [applyDetail, awaitNextSocketRequest, composer, conversationId, reportError, setAiChatOpen, updateSocketRequest],
  );

  /** Retry the failed HITL action or generation. */
  const retryFailedOperation = useCallback(() => {
    const failedAction = composer.status === COMPOSER_STATUS.generationError ? composer.failedAction : undefined;
    if (failedAction) {
      setComposer({ status: COMPOSER_STATUS.action, action: failedAction, armed: false });
      return;
    }
    void retryGeneration();
  }, [composer, retryGeneration]);

  const refineLimitHit = refinesUsed >= maxResponseRefines;
  const regenerateReady = detail ? canRegenerateFromApi(detail) : false;
  // Same cap the backend checks, so the button and the API agree. Null while it is unknown.
  const blueprintLimitHit =
    maxBlueprintRefines === null ? null : (detail?.ticket.blueprint_retries_used ?? 0) >= maxBlueprintRefines;

  const recordFeedback = useCallback(
    async (
      verdict: FeedbackVerdict,
      reasons: string[],
      otherText: string | null,
      rememberScope: string | null,
      responseId?: string,
    ) => {
      // A hand-written reply has no AI version, so feedback goes on the ticket's event.
      const targetId = responseId ?? versions[versions.length - 1]?.id ?? detail?.ticket.current_ai_event_log_id;
      if (conversationId === null || targetId == null) return false;
      try {
        await sentinelWorkspaceService.recordResponseFeedback(conversationId, String(targetId), {
          verdict,
          reasons,
          otherText,
          rememberScope,
        });
        return true;
      } catch (error) {
        reportError(error, 'response_feedback');
        toast.error('Unable to save feedback. The reply was not sent.');
        return false;
      }
    },
    [conversationId, detail?.ticket.current_ai_event_log_id, reportError, versions],
  );

  /** Refine the active response — style preset or free-text prompt. */
  const refineResponse = useCallback(
    async (input: { command?: string; prompt?: string }, label: string) => {
      // One refine at a time — chip mashing while one is in flight is ignored.
      if (conversationId === null || refineLimitHit || refineBusyRef.current) return;
      refineBusyRef.current = true;
      awaitingRefineRef.current = true;
      refineBaselineIdRef.current = versions.at(-1)?.id ?? null;
      const runId = runIdRef.current;
      lastRefineRef.current = { input, label };
      setAiChatOpen(true);
      setRefineError(null);
      setPendingRefine({ label, trace: [], stepsDone: 0 });
      awaitNextSocketRequest();
      try {
        const dto = await sentinelWorkspaceService.refineResponse(
          conversationId,
          input,
          updateSocketRequest,
          // Streams the run's persisted steps into the refine card on every poll tick.
          (fresh) => {
            if (runId !== runIdRef.current) return;
            const tick = mergeTraceSteps(inflightTraceFromApi(fresh), liveTraceRef.current);
            setPendingRefine((previous) => {
              if (!previous) return previous;
              const merged = mergeTraceSteps(previous.trace, tick);
              return { ...previous, trace: merged, stepsDone: merged.length };
            });
          },
        );
        if (runId !== runIdRef.current) return;
        const artifact = SentinelWorkspaceAdapter.artifactFromApi(dto);
        setRefinesUsed(dto.ticket.response_retries_used);
        // Only a replan leaves the refine still owed an answer.
        awaitingRefineRef.current = artifact.kind === 'blueprint' || artifact.kind === 'hitl';
        if (artifact.kind === 'response') {
          setRefineLabels((prev) => ({ ...prev, [artifact.response.id]: label }));
          setVersions((prev) => [...prev, artifact.response]);
          setComposer({ status: COMPOSER_STATUS.response, active: artifact.response });
        } else if (artifact.kind === 'blueprint' || artifact.kind === 'hitl') {
          // The answer comes later, so keep the history the server already has.
          const history = SentinelWorkspaceAdapter.responseHistoryFromApi(dto);
          if (history.versions.length > 0) {
            setVersions(history.versions);
            setRefineLabels(history.refineLabels);
          }
          setAiChatOpen(false);
          setComposer(
            artifact.kind === 'blueprint'
              ? { status: COMPOSER_STATUS.blueprint, blueprint: artifact.blueprint, refineOpen: false }
              : { status: COMPOSER_STATUS.action, action: artifact.action, armed: false },
          );
        } else if (artifact.kind === 'error') {
          setRefineError(artifact.message);
        }
      } catch (error) {
        if (runId === runIdRef.current) {
          awaitingRefineRef.current = false;
          setRefineError(error instanceof Error ? error.message : 'Something went wrong');
          reportError(error, 'response_refine');
        }
      } finally {
        refineBusyRef.current = false;
        if (runId === runIdRef.current) setPendingRefine(null);
      }
    },
    [awaitNextSocketRequest, conversationId, refineLimitHit, reportError, setAiChatOpen, updateSocketRequest, versions],
  );

  /** Retry the refine that just failed. */
  const retryRefine = useCallback(() => {
    const last = lastRefineRef.current;
    if (last) void refineResponse(last.input, last.label);
  }, [refineResponse]);

  /** Draft again from current ticket data. No prompt, so the server runs fresh instead of rewording the draft. */
  const regenerateResponse = useCallback(() => {
    void refineResponse({ command: 'generate' }, REGENERATE_LABEL);
  }, [refineResponse]);

  /** Put a response (any version) into the editor. */
  const useResponse = useCallback(
    (responseId?: string) => {
      const source = responseId ? versions.find((v) => v.id === responseId) : versions[versions.length - 1];
      if (!source) return;
      setAiChatOpen(false);
      // Citation markers stay in the card view only — never in the outgoing reply.
      const draftMarkdown = nestBulletsUnderNumbers(source.markdown.replace(/\s*\[\[\d+\]\]/g, ''));
      setComposer((previous) => ({
        status: COMPOSER_STATUS.editing,
        draftMarkdown,
        sourceResponseId: source.id,
        ...(previous.status === COMPOSER_STATUS.automated ? { automated: previous } : {}),
      }));
    },
    [setAiChatOpen, versions],
  );

  /** Leave the unsent reply editor and restore the response that opened it. */
  const returnToResponse = useCallback(() => {
    setAiChatOpen(false);
    setComposer((previous) => {
      if (previous.status !== COMPOSER_STATUS.editing) return previous;
      if (previous.automated) return previous.automated;
      const source = previous.sourceResponseId
        ? versions.find((response) => response.id === previous.sourceResponseId)
        : versions.at(-1);
      return source ? { status: COMPOSER_STATUS.response, active: source } : previous;
    });
  }, [setAiChatOpen, versions]);

  /** The send gate's check of `markdown`, kept for the Send that follows. Null when the check could not run. */
  const checkResponse = useCallback(
    async (markdown: string): Promise<GateCheckDto | null> => {
      if (conversationId === null) return null;
      const runId = runIdRef.current;
      try {
        const check = await sentinelWorkspaceService.checkResponse(conversationId, markdown);
        if (runId !== runIdRef.current) return null;
        setGateCheck({ content: markdown.trim(), check });
        return check;
      } catch (error) {
        // The Send still goes through the gate on the server, so a failed check only skips the early look.
        reportError(error, 'check_reply');
        return null;
      }
    },
    [conversationId, reportError],
  );

  /** The override of `markdown` on this button, or null. Its close fields go out again with a retry. */
  const overrideFor = useCallback(
    (markdown: string, close: boolean): GateOverride | null =>
      overridden?.content === markdown.trim() && overridden.close === close ? overridden : null,
    [overridden],
  );

  const send = useCallback(
    async (
      markdown: string,
      close: boolean,
      files: File[] = [],
      ticketFields: TicketFieldValue[] = [],
      overrideReason?: string,
    ) => {
      if (conversationId === null) return false;
      const runId = runIdRef.current;
      const content = markdown.trim();
      const reason = overrideReason ?? overrideFor(content, close)?.reason;
      if (overrideReason) setOverridden({ content, close, reason: overrideReason, ticketFields });
      // A Send on a ticket nobody owns makes it this Sentinel's: no toast or ding for that.
      if (selectedTicketId !== null) markOwnTicketAction(selectedTicketId);
      try {
        const signature = JSON.stringify({
          markdown,
          close,
          files: files.map((file) => [file.name, file.size, file.lastModified, file.type]),
          ticketFields,
        });
        if (sendAttemptRef.current?.signature !== signature) {
          sendAttemptRef.current = { signature, key: crypto.randomUUID(), attachments: [] };
        }
        const attempt = sendAttemptRef.current;
        if (files.length && attempt.attachments.length === 0) {
          attempt.attachments = await sentinelWorkspaceService.uploadResponseAttachments(conversationId, files);
        }
        const dispatch = (checkId: string | null): Promise<SendResponseOutcome> =>
          sentinelWorkspaceService.sendResponse(
            conversationId,
            {
              content: markdown,
              close,
              ...(attempt.attachments.length ? { attachments: attempt.attachments } : {}),
              ...(close && ticketFields.length ? { ticketFields } : {}),
              ...(checkId ? { gate: { checkId, ...(reason ? { overrideReason: reason } : {}) } } : {}),
            },
            attempt.key,
          );
        const keptCheckId = gateCheck?.content === content ? gateCheck.check.checkId : null;
        let outcome = await dispatch(keptCheckId);
        if (outcome.status === 'gate_blocked' && outcome.errorCode === 'GATE_STALE') {
          // The kept check was of other content: check this content once, then send on that check.
          const fresh = await sentinelWorkspaceService.checkResponse(conversationId, markdown);
          if (runId !== runIdRef.current) return false;
          setGateCheck({ content, check: fresh });
          outcome =
            fresh.verdict === 'block' && !reason
              ? { status: 'gate_blocked', errorCode: 'GATE_BLOCKED', check: fresh }
              : await dispatch(fresh.checkId);
        }
        if (runId !== runIdRef.current) return false;
        if (outcome.status === 'gate_blocked') {
          if (outcome.check) setGateCheck({ content, check: outcome.check });
          setModal(WORKSPACE_MODAL.sendGate);
          return false;
        }
        sendAttemptRef.current = null;
        setSentReply(markdown);
        setSentResponseId(composer.status === COMPOSER_STATUS.editing ? composer.sourceResponseId : null);
        setComposer({ status: COMPOSER_STATUS.done, label: close ? 'resolved' : 'answered' });
        return true;
      } catch (error) {
        reportError(error, 'send_reply');
        if (error instanceof SentinelWorkspaceError && error.code?.startsWith('DISPATCH_')) {
          setComposer({ status: COMPOSER_STATUS.generationError, message: error.message, retryable: false });
          // The composer now shows the error, and the reply cannot be sent again from the modal.
          setModal(null);
          void queryClient.invalidateQueries({ queryKey: ['sentinel-workspace', 'conversation'] });
        }
        toast.error(getApiErrorMessage(error, 'Unable to send the reply. Please try again.'));
        return false;
      }
    },
    [composer, conversationId, gateCheck, overrideFor, reportError, selectedTicketId],
  );

  /** Add a private note to the thread — the composer stays open. */
  const addNote = useCallback(
    async (markdown: string) => {
      if (conversationId === null || !markdown.trim()) return false;
      const runId = runIdRef.current;
      try {
        await sentinelWorkspaceService.addNote(conversationId, { content: markdown });
        if (runId !== runIdRef.current) return false;
        setPrivateNotes((prev) => [
          ...prev,
          {
            id: -(prev.length + 2),
            markdown,
            from: 'agent',
            isPrivate: true,
            senderName: 'Kim AI',
            createdAt: new Date().toISOString(),
          },
        ]);
        return true;
      } catch (error) {
        reportError(error, 'add_note');
        toast.error(getApiErrorMessage(error, 'Unable to add the note. Please try again.'));
        return false;
      }
    },
    [conversationId, reportError],
  );

  /** Write the reply by hand; the ticket stays with Sentinel and keeps its panel data. */
  const handleManually = useCallback(() => {
    setComposer({ status: COMPOSER_STATUS.editing, draftMarkdown: '', sourceResponseId: null });
  }, []);

  // A retry of the same reject reuses its key, so the backend does not add the note and tags to the
  // helpdesk a second time (Sentinel ticket 9575). A changed input is a new reject with a new key.
  const pendingRejectRef = useRef<{ fingerprint: string; key: string } | null>(null);
  const reject = useCallback(
    async (input: RejectInput) => {
      if (conversationId === null) return false;
      const fingerprint = JSON.stringify({ conversationId, input });
      if (pendingRejectRef.current?.fingerprint !== fingerprint) {
        pendingRejectRef.current = { fingerprint, key: crypto.randomUUID() };
      }
      try {
        // Defaults keep an older backend, which returns only `status`, from failing a successful reject.
        const { tagsNotAdded = [], noteNotAdded = false } = await sentinelWorkspaceService.reject(
          conversationId,
          input,
          pendingRejectRef.current.key,
        );
        pendingRejectRef.current = null;
        setModal(null);
        if (tagsNotAdded.length > 0) toast.warning(`Unassigned, but these tags weren't added: ${tagsNotAdded.join(', ')}`);
        if (noteNotAdded) toast.warning("Unassigned, but the private note wasn't added to the helpdesk");
        return true;
      } catch (error) {
        reportError(error, 'reject_ticket');
        toast.error(getApiErrorMessage(error, 'Unable to reject the ticket. Please try again.'));
        return false;
      }
    },
    [conversationId, reportError],
  );

  const assign = useCallback(
    async (reason: RejectInput['reason'], providerAgentId: string) => {
      if (conversationId === null) return false;
      try {
        await sentinelWorkspaceService.assign(conversationId, { reason, providerAgentId });
        setModal(null);
        return true;
      } catch (error) {
        reportError(error, 'assign_ticket');
        toast.error(getApiErrorMessage(error, 'Unable to assign the ticket. Please try again.'));
        return false;
      }
    },
    [conversationId, reportError],
  );

  return {
    composer,
    modal,
    setModal,
    versions,
    sentReply,
    sentResponseId,
    privateNotes,
    aiChat: { open: aiChatOpen, setOpen: setAiChatOpen, pendingRefine, refineError, refineLabels, rejectDraftAction },
    blueprintRefined,
    refineLimitHit,
    // The server takes a new refine only from a settled response.
    refineReady: detail?.ticket.status === 'response_generated',
    regenerateReady,
    blueprintLimitHit,
    trace,
    toolTrace,
    sendGate: { enabled: sendGateEnabled, check: gateCheck?.check ?? null, overrideFor },
    actions: {
      setRefineOpen,
      refineBlueprint,
      generate,
      generateAgain,
      retryGeneration,
      retryFailedOperation,
      handleManually,
      toggleGenerationExpanded,
      armAction,
      confirmAction,
      rejectActionWithDraft,
      openRejectDraft,
      closeRejectDraft,
      recordFeedback,
      refineResponse,
      retryRefine,
      regenerateResponse,
      useResponse,
      returnToResponse,
      checkResponse,
      send,
      addNote,
      reject,
      assign,
    },
  };
}

export type SentinelComposerApi = ReturnType<typeof useSentinelComposer>;

interface CustomerTicketsState {
  tickets: CustomerTicketDto[];
  loading: boolean;
  hasMore: boolean;
}

const NO_CUSTOMER_TICKETS: CustomerTicketsState = { tickets: [], loading: false, hasMore: false };

/** Loads the customer's history for the open ticket; a failed load shows nothing rather than an error. */
export function useCustomerTickets(ticketId: number | null): CustomerTicketsState {
  const [state, setState] = useState<CustomerTicketsState>(NO_CUSTOMER_TICKETS);

  useEffect(() => {
    if (ticketId === null) {
      setState(NO_CUSTOMER_TICKETS);
      return;
    }
    let cancelled = false;
    setState({ ...NO_CUSTOMER_TICKETS, loading: true });
    sentinelWorkspaceService
      .getCustomerTickets(ticketId)
      .then((page) => !cancelled && setState({ tickets: page.tickets, loading: false, hasMore: page.hasMore }))
      .catch(() => !cancelled && setState(NO_CUSTOMER_TICKETS));
    return () => {
      cancelled = true;
    };
  }, [ticketId]);

  return state;
}
